import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import BASE_URL from "../api";
import { AppDispatch, RootState } from "../../../store/store";
import {
    DealRoom,
    DealRoomState,
    CreateDealRoomPayload,
    AssignLawyerPayload,
    LawyerResponsePayload,
    UpdateDealRoomStagePayload,
} from "../types/deaalroom";

/* =======================
   INITIAL STATE
======================= */

const initialState: DealRoomState = {
    dealRooms: [],
    dealRoom: null,
    loading: false,
    error: null,
    success: null,
};

/* =======================
   API HELPER
======================= */

async function api<T>(
    path: string,
    method: "GET" | "POST" | "PUT" | "DELETE",
    token?: string,
    body?: unknown
): Promise<T> {
    const res = await fetch(`${BASE_URL}${path}`, {
        method,
        headers: {
            "Content-Type": "application/json",
            ...(token && {
                Authorization: `Bearer ${token}`,
            }),
        },
        body: body ? JSON.stringify(body) : undefined,
    });

    const data = await res.json();

    if (!res.ok) {
        const message =
            data?.message ||
            Object.values(data?.errors || {}).flat()?.[0] ||
            "Request failed";

        throw new Error(message);
    }

    return data as T;
}

/* =======================
   MULTIPART API
======================= */

async function apiForm<T>(
    path: string,
    method: "POST" | "PUT",
    token: string,
    body: FormData
): Promise<T> {
    const res = await fetch(`${BASE_URL}${path}`, {
        method,
        headers: {
            Authorization: `Bearer ${token}`,
        },
        body,
    });

    const data = await res.json();

    if (!res.ok) {
        throw new Error(
            data?.message ??
            Object.values(data?.errors || {}).flat()?.[0] ??
            "Upload failed"
        );
    }

    return data as T;
}

const getToken = (state: RootState) => {
    return state.auth.access_token;
};

/* =======================
   SLICE
======================= */

const dealRoomSlice = createSlice({
    name: "dealRoom",
    initialState,
    reducers: {
        start(state) {
            state.loading = true;
            state.error = null;
            state.success = null;
        },

        fail(state, action: PayloadAction<any>) {
            state.loading = false;
            state.error = action.payload?.message || action.payload;
        },

        setSuccess(state, action: PayloadAction<string>) {
            state.loading = false;
            state.success = action.payload;
        },

        setDealRooms(
            state,
            action: PayloadAction<DealRoom[]>
        ) {
            state.loading = false;
            state.dealRooms = action.payload;
        },

        setDealRoom(
            state,
            action: PayloadAction<DealRoom>
        ) {
            state.loading = false;
            state.dealRoom = action.payload;
        },

        addDealRoom(
            state,
            action: PayloadAction<DealRoom>
        ) {
            state.loading = false;
            state.dealRooms.unshift(action.payload);
            state.dealRoom = action.payload;
        },

        updateDealRoom(
            state,
            action: PayloadAction<DealRoom>
        ) {
            state.loading = false;

            state.dealRooms = state.dealRooms.map((d) =>
                d.id === action.payload.id
                    ? {
                        ...d,
                        ...action.payload,
                    }
                    : d
            );

            if (
                state.dealRoom?.id ===
                action.payload.id
            ) {
                state.dealRoom = {
                    ...state.dealRoom,
                    ...action.payload,
                };
            }
        },

        clearDealRoom(state) {
            state.dealRoom = null;
            state.error = null;
            state.success = null;
        },
    },
});

export const {
    start,
    fail,
    setSuccess,
    setDealRooms,
    setDealRoom,
    addDealRoom,
    updateDealRoom,
    clearDealRoom,
} = dealRoomSlice.actions;

export default dealRoomSlice.reducer;

/* =======================
   THUNKS
======================= */

/**
 * =======================
 * 📌 GET ALL DEAL ROOMS
 * =======================
 */
export const fetchDealRooms =
    () =>
        async (
            dispatch: AppDispatch,
            getState: () => RootState
        ) => {

            dispatch(start());

            try {

                const token = getToken(getState());

                if (!token) {
                    throw new Error("Unauthorized");
                }

                const data = await api<{
                    data: DealRoom[];
                    message: string;
                }>(
                    "/user/deal-room",
                    "GET",
                    token
                );

                dispatch(setDealRooms(data.data));
                dispatch(setSuccess(data.message));

                return data;

            } catch (err: any) {

                dispatch(fail(err));
                throw err;

            }
        };

// GET DEAL ROOM ID
export const fetchDealRoom =
    (id: string) =>
        async (
            dispatch: AppDispatch,
            getState: () => RootState
        ) => {

            dispatch(start());

            try {

                const token = getToken(getState());

                if (!token) {
                    throw new Error("Unauthorized");
                }

                const data = await api<{
                    data: DealRoom;
                    message: string;
                }>(
                    `/user/deal-room/${id}`,
                    "GET",
                    token
                );

                dispatch(setDealRoom(data.data));

                return data;

            } catch (err: any) {

                dispatch(fail(err));
                throw err;

            }
        };



/**
 * 📌 CREATE DEAL ROOM
 */
export const createDealRoom =
    (body: CreateDealRoomPayload) =>
        async (
            dispatch: AppDispatch,
            getState: () => RootState
        ) => {
            dispatch(start());

            try {
                const token = getToken(getState());

                if (!token) throw new Error("Unauthorized");

                const data = await api<{
                    data: DealRoom;
                    message: string;
                }>(
                    "/user/deal-room",
                    "POST",
                    token,
                    body
                );

                dispatch(addDealRoom(data.data));
                dispatch(setSuccess(data.message));

                return data;
            } catch (err: any) {
                dispatch(fail(err));
                throw err;
            }
        };

/**
 * =======================
 * 📌 UPDATE DEAL ROOM STAGE
 * =======================
 */
export const updateDealRoomStage =
    (
        id: string | number,
        body: UpdateDealRoomStagePayload
    ) =>
        async (
            dispatch: AppDispatch,
            getState: () => RootState
        ) => {
            dispatch(start());

            try {
                const token = getToken(getState());

                if (!token) {
                    throw new Error("Unauthorized");
                }

                const data = await api<{
                    data: DealRoom;
                    message: string;
                }>(
                    `/user/deal-room/${id}/stage`,
                    "PUT",
                    token,
                    body
                );

                dispatch(updateDealRoom(data.data));
                dispatch(setSuccess(data.message));

                return data;
            } catch (err: any) {
                dispatch(fail(err));
                throw err;
            }
        };

export const updateDealRoomDocumentsm =
    (
        id: string | number,
        formData: FormData
    ) =>
    async (
        dispatch: AppDispatch,
        getState: () => RootState
    ) => {

        dispatch(start());

        try {

            const token = getToken(getState());

            if (!token) {
                throw new Error("Unauthorized");
            }

            // Ensure current_stage is always sent
            if (!formData.has("current_stage")) {
                formData.append("current_stage", "dataroom");
            }

            // Debug
console.group("Deal Room Upload Payload");

formData.forEach((value, key) => {
    console.log(key, value);
});

console.groupEnd();

            const response = await fetch(
                `${BASE_URL}/user/deal-room/${id}/documents`,
                {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                }
            );

            const text = await response.text();

            console.group("Deal Room Upload Response");
            console.log("Status:", response.status);
            console.log("Response:", text);
            console.groupEnd();

            let data: any = {};

            try {
                data = JSON.parse(text);
            } catch {
                throw new Error(
                    `Server returned a non-JSON response (${response.status}).`
                );
            }

            if (!response.ok) {
                throw new Error(
                    data?.message ??
                    data?.error ??
                    (data?.errors
                        ? Object.values(data.errors).flat().join("\n")
                        : "Upload failed")
                );
            }

            dispatch(updateDealRoom(data.data));
            dispatch(setSuccess(data.message));

            return data;

        } catch (error) {

            const err =
                error instanceof Error
                    ? error
                    : new Error("Upload failed");

            dispatch(fail(err));
            throw err;

        }
    };

        export const updateDealRoomDocumentserrodisplay =
    (id: string | number, formData: FormData) =>
    async (
        dispatch: AppDispatch,
        getState: () => RootState
    ) => {
        dispatch(start());

        try {
            const token = getToken(getState());

            if (!token) {
                throw new Error("Unauthorized");
            }

            const response = await fetch(
                `${BASE_URL}/user/deal-room/${id}/documents`,
                {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    body: formData,
                }
            );

            const responseText = await response.text();

            console.group("Deal Room Document Upload");
            console.log("Status:", response.status);
            console.log("Response:", responseText);
            console.groupEnd();

            let data: any = null;

            try {
                data = JSON.parse(responseText);
            } catch {
                throw new Error(
                    `Server returned a non-JSON response (${response.status}).\n\n${responseText.substring(
                        0,
                        500
                    )}`
                );
            }

            if (!response.ok) {
                const message =
                    data?.message ??
                    (data?.errors
                        ? Object.values(data.errors).flat().join("\n")
                        : null) ??
                    `Request failed (${response.status})`;

                throw new Error(message);
            }

            dispatch(updateDealRoom(data.data));
            dispatch(setSuccess(data.message));

            return data;
        } catch (error) {
            const err =
                error instanceof Error
                    ? error
                    : new Error("Upload failed");

            dispatch(fail(err));

            throw err;
        }
    };

//UPDATE FOR DOCUMENTS
export const updateDealRoomDocuments =
    (
        id: string | number,
        formData: FormData
    ) =>
        async (
            dispatch: AppDispatch,
            getState: () => RootState
        ) => {

            dispatch(start());

            try {

                const token = getToken(getState());

                if (!token) {
                    throw new Error("Unauthorized");
                }

                const response = await fetch(
                    `${BASE_URL}/user/deal-room/${id}/documents`,
                    {
                        method: "POST",
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        body: formData,
                    }
                );

                const data = await response.json();

                // if (!response.ok) {
                //     throw new Error(data.message);
                // }
                if (!response.ok) {
                    let message = "Upload failed";

                    if (data?.errors) {
                        message = Object.values(data.errors)
                            .flat()
                            .join("\n");
                    } else if (data?.message) {
                        message = data.message;
                    }

                    throw new Error(message);
                }

                dispatch(updateDealRoom(data.data));
                dispatch(setSuccess(data.message));

                return data;

            } catch (err: any) {

                dispatch(fail(err));
                throw err;

            }
        };



export type { DealRoomState };